import { ICoinflipGame } from '@api/types/Coinflip';
import { useLazyQuery } from '@apollo/react-hooks';
import { GET_COINFLIP_GAMES } from '@graphql/Queries';
import reduxAction from '@redux/reduxAction';
import { toastErrorMessage } from '@utils/utils';

function isGameFinished(game: ICoinflipGame) {
    return (
        game.game_metadata.status === 'finished' &&
        game.game_metadata.ended_at &&
        Math.floor(Date.now() / 1000) - game.game_metadata.ended_at >= 12
    );
}

function coinflipSort(a: ICoinflipGame, b: ICoinflipGame) {
    const atime = isGameFinished(a)
        ? a.game_metadata.ended_at
        : a.game_metadata.created_at;
    const btime = isGameFinished(b)
        ? b.game_metadata.ended_at
        : b.game_metadata.created_at;

    if (atime > btime) return -1;
    if (atime < btime) return 1;
    return 0;
}

export function filterGames(games: ICoinflipGame[]): {
    recentGames: string[];
    oldGames: string[];
} {
    const recentGames: string[] = [];
    const oldGames: string[] = [];

    games.sort(coinflipSort).forEach((game) => {
        if (isGameFinished(game)) {
            oldGames.push(game.uid);
        } else {
            recentGames.push(game.uid);
        }
    });

    return {
        recentGames,
        oldGames,
    };
}

export function reorderGames(games: ICoinflipGame[]) {
    const newGames: Record<string, ICoinflipGame> = {};

    games.forEach((game) => {
        if (!(game.uid in newGames)) {
            newGames[game.uid] = game;
        }
    });

    return newGames;
}

export default function useFetchCoinflipGames(): () => Promise<void> {
    const [fetchGames] = useLazyQuery(GET_COINFLIP_GAMES, {
        onCompleted: (data) => {
            const games: ICoinflipGame[] = [...data.getAllCoinflipGames];
            reduxAction({
                type: 'SET_COINFLIP_GAMES',
                arg: {
                    ...reorderGames(games),
                },
            });
        },
        onError: (error) => {
            toastErrorMessage(error.message);
        },
    });

    async function getGames() {
        await fetchGames({});
    }

    return getGames;
}
